import { useMemo } from 'react';
import React from 'react';
import { Field } from '@tw/tw-component-library';
import {
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';
import { FileDisplay } from '../../components/FileDisplay';

// ============= Component =============
export const usePaymentConfirmationSchema = (
  formState: FormState
): FormSchemaConfig => {
  return useMemo<FormSchemaConfig>(() => {
    // For payment_confirmation section, check if user is creator instead of assignment
    const isEditAllowed =
      formState.data?.userAssignedSections?.includes('payment_confirmation') ===
      true;

    // Create the main section that matches EnhancedGroupedSection type
    const paymentConfirmationSection: EnhancedGroupedSection = {
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: 'Payment Confirmation',
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'payment_confirmation',
              label: 'Has this payment been actioned?',
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value) =>
                !value
                  ? 'Please select whether this payment has been actioned'
                  : undefined,
              disabled:
                formState.completedSections?.includes('payment_confirmation') ||
                !isEditAllowed,
            },
            {
              type: 'textArea',
              name: 'payment_rejection_details',
              label: 'Rejection Details',
              required: formState.data.payment_confirmation === 'no',
              validate: (value) => {
                const isRequired = formState.data.payment_confirmation === 'no';
                if (!isRequired) return undefined;
                return !value
                  ? 'Please enter the rejection details'
                  : undefined;
              },
              disabled:
                formState.completedSections?.includes('payment_confirmation') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'payment_confirmation',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            },
            // Show existing files when form is completed
            ...(formState.completedSections?.includes('payment_confirmation') &&
            formState.data?.payment_upload_documentation
              ? [
                  {
                    type: 'customComponent' as const,
                    customComponent: React.createElement(FileDisplay, {
                      fileName: formState.data?.payment_upload_documentation,
                      workflowId: formState.data?.id,
                    }),
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'payment_confirmation',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                    name: 'payment_upload_documentation_display',
                    label: 'Supporting Documentation:',
                  },
                ]
              : []),
            // Show existing files when rejected (but still allow uploads)
            ...(formState.isRejected &&
            formState.data?.payment_upload_documentation
              ? [
                  {
                    type: 'customComponent' as const,
                    customComponent: React.createElement(FileDisplay, {
                      fileName: formState.data?.payment_upload_documentation,
                      workflowId: formState.data?.id,
                    }),
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'payment_confirmation',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                    name: 'payment_upload_documentation_display_rejected',
                    label: 'Previously Uploaded Supporting Documentation:',
                  },
                ]
              : []),
            // Always show file upload field (unless form is completed and not rejected)
            ...(!formState.completedSections?.includes(
              'payment_confirmation'
            ) || formState.isRejected
              ? [
                  {
                    type: 'file' as const,
                    name: 'payment_upload_documentation',
                    label: formState.isRejected
                      ? 'Upload Additional Supporting Documentation (if needed):'
                      : 'Upload supporting Documentation',
                    tooltip: 'Document Viewer only supports: .pdf',
                    extras: {
                      file: {
                        multiple: true,
                      },
                    } as Field['extras'],
                    disabled:
                      (formState.completedSections?.includes(
                        'payment_confirmation'
                      ) &&
                        !formState.isRejected) ||
                      !isEditAllowed,
                    dependencies: [
                      {
                        type: 'show' as const,
                        logicalOperator: 'AND' as const,
                        conditions: [
                          {
                            field: 'payment_confirmation',
                            operator: 'equals' as const,
                            value: 'yes',
                          },
                        ],
                      },
                    ],
                  },
                ]
              : []),
            {
              type: 'textArea',
              name: 'payment_comments',
              label: 'Any other comments',
              disabled:
                formState.completedSections?.includes('payment_confirmation') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'payment_confirmation',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'number',
              name: 'payment_total_interest',
              label: 'Total Payment Amount remitted including Interest',
              value: formState.data.payment_total,
              required: formState.data.payment_confirmation === 'yes',
              validate: (value) => {
                const isRequired =
                  formState.data.payment_confirmation === 'yes';
                if (!isRequired) return undefined;
                return !value
                  ? 'Please enter the total payment amount remitted including Interest'
                  : undefined;
              },
              extras: {
                textInput: {
                  formatCurrency: true,
                },
              },
              disabled:
                formState.completedSections?.includes('payment_confirmation') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'payment_confirmation',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'interest',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [paymentConfirmationSection],
    };
  }, [formState]);
};
